import * as angular from 'angular';
import * as d3 from 'd3';
import * as moment from 'moment';

import { ComponentDefinition, Dictionary, Group } from '../../../../models';
import { StepProcessingTime } from '../StepProcessingTime';
import { IMargins, SvgManager } from '../../../../framework';
import { momentFormat } from '../../../../common/constants';

interface RectFormat {
    color: string;
    item: StepProcessingTime & {
        formattedDate: string;
        formattedTime: string;
        group: Group;
    };
    height: number;
    width: number;
    x: number;
    y: number;
}

class HeatMapController implements angular.IComponentController {

    private _innerMargins = {
        top: 20,
        left: 150,
        right: 20,
        bottom: 20
    };
    private _svgManager: SvgManager;

    readonly data: StepProcessingTime[];
    readonly groups: Dictionary<Group>;
    readonly isLoading: boolean;

    static $inject = ['$translate'];

    constructor(private _$translate: angular.translate.ITranslateService) {
    }

    $onInit() {
        const margins: IMargins = {
            marginTop: 0,
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0
        };
        const svgManager = new SvgManager('performance-heat-map', margins);
        this._svgManager = svgManager;
        const svg = svgManager.container();
        const container = svg.append('g')
            .classed('container', true)
            .attr('transform', `translate(${this._innerMargins.left}, ${this._innerMargins.top})`);
        container.append('g')
            .classed('steps-axis', true)
            .classed('axis', true)
            ;
        container.append('g')
            .classed('groups-axis', true)
            .classed('axis', true)
            ;
        container.append('g')
            .classed('rectangles', true)
            ;
        this.render();
        svgManager.addResizeFunction(() => {
            this.render();
        });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['data']?.currentValue) {
            this.render();
        }
        if (changesObj['groups']?.currentValue) {
            this.render();
        }
    }

    private render() {
        if (this.data && this._svgManager && this.groups) {
            const size = this._svgManager.getSize();
            const availableWidth = size.width - this._innerMargins.left - this._innerMargins.right;
            const steps = d3.nest<StepProcessingTime>()
                .key(d => d.step)
                .entries(this.data)
                .map(d => d.key);
            const stepsScale = d3.scale.ordinal<string, number>()
                .domain(steps)
                .rangeBands([0, availableWidth]);
            const stepsAxesGroup = this._svgManager.container()
                .select('.steps-axis');
            const stepsAxis = d3.svg.axis()
                .orient('top')
                .scale(stepsScale)
                .tickFormat(d => this._$translate.instant(d));
            stepsAxesGroup.call(stepsAxis);

            const nested: { key: string, values: StepProcessingTime[] }[] = d3.nest<StepProcessingTime>()
                .key(d => d.groupGuid)
                .entries(this.data);
            const availableHeight = nested.length * 20;
            this._svgManager.setHeight(availableHeight + this._innerMargins.top + this._innerMargins.bottom);
            const groupsScale = d3.scale.ordinal<string, number>()
                .domain(nested.map(d => this.groups[d.key]?.uniqueName))
                .rangeBands([0, availableHeight]);
            const groupsAxesGroup = this._svgManager.container()
                .select('.groups-axis');
            const groupsAxis = d3.svg.axis()
                .orient('left')
                .scale(groupsScale);
            groupsAxesGroup.call(groupsAxis);


            const rectHeight = groupsScale.rangeBand();
            const rectWidth = stepsScale.rangeBand();

            const colorScale = d3.scale.linear<string, string>()
                .domain([0, d3.max(this.data, d => d.elapsedSeconds)])
                .interpolate(d3.interpolateRgb)
                .range(['#fafafa', 'red']);
            const fmt = d3.format('.2f');
            const rects: RectFormat[] = [].concat(...nested.map(n => {
                const yPos = groupsScale(this.groups[n.key]?.uniqueName);
                return n.values.map(d => {
                    const xPos = stepsScale(d.step);
                    const color = colorScale(d.elapsedSeconds);
                    return {
                        x: xPos,
                        y: yPos,
                        width: rectWidth,
                        height: rectHeight,
                        color,
                        item: Object.assign({}, d, {
                            group: this.groups[n.key],
                            formattedDate: moment(d.timeStamp).format(momentFormat),
                            formattedTime: fmt(d.elapsedSeconds)
                        })
                    };
                });
            }));

            const dataBound = this._svgManager.container()
                .select('g.rectangles')
                .selectAll('.rect')
                .data(rects);
            dataBound
                .exit()
                .remove();
            const enterSelection = dataBound
                .enter()
                .append('g')
                .classed('rect', true);
            enterSelection.append('rect')
                .style('pointer-events', 'all')
                .append('title');
            dataBound.select('title')
                .text(d => {
                    const groupName = d.item.group?.uniqueName ? ` ${d.item.group?.uniqueName} -` : '';
                    return `${d.item.formattedDate} -${groupName} ${this._$translate.instant(d.item.step)}: ${d.item.formattedTime}s`;
                });
            dataBound
                .attr('transform', d => `translate(${d.x}, ${d.y})`)
                .select('rect')
                .attr('x', 0)
                .attr('y', 0)
                .attr('width', d => d.width)
                .attr('height', d => d.height)
                .style('fill', d => d.color);

        }
    }
}

export const wtHeatMap: ComponentDefinition = {
    componentId: 'wtHeatMap',
    controller: HeatMapController,
    template: require('./heatMap.component.html'),
    controllerAs: 'vm',
    bindings: {
        data: '<',
        groups: '<',
        isLoading: '<'
    }
};